import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { Observable } from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingAttributeComponent, RoleBadgeModule, UserAvatarModule } from '@shared/ui';
import { TranslateModule } from '@ngx-translate/core';
import { RoleData } from '@telespot/authentication/ui';

export enum UserColumn {
  AVATAR = 'avatar',
  USERNAME = 'username',
  AFFILIATE_SINCE = 'affiliateSince',
  ROLES = 'roles',
  WORKSPACES = 'workspaces',
  MEMBER = 'member',
  EDIT = 'edit',
  DELETE = 'delete',
}

export interface UserListItem {
  id: string;
  username: string;
  emailVerified?: boolean;
  avatarUrl: string | undefined;
  affiliationDate: Date;
  roles?: RoleData[];
  numWorkspaces?: number;
  isMemberOfWorkspace?: boolean;
}

@Component({
  selector: 'ts-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    UserAvatarModule,
    RoleBadgeModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    LoadingAttributeComponent,
  ],
})
export class UserListComponent implements OnChanges {
  @Input() items: UserListItem[] = [];
  @Input() loading = true;
  @Input() noUsers = false;
  @Input() canEdit = false;
  @Input() columns: UserColumn[] = [UserColumn.USERNAME, UserColumn.ROLES, UserColumn.EDIT, UserColumn.DELETE];

  // Store the container height when loading starts to prevent layout jumps
  public containerHeight: number | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }

  get allowedColumns(): UserColumn[] {
    if (this.canEdit) {
      return this.columns;
    }

    // Remove EDIT and DELETE columns if not editable
    return this.columns.filter((col) => col !== UserColumn.EDIT && col !== UserColumn.DELETE);
  }

  @Output() editItem = new EventEmitter<UserListItem>();
  @Output() itemRemoved = new EventEmitter<UserListItem>();
  @Output() membershipToggle = new EventEmitter<UserListItem>();

  public readonly showMobileLayout$: Observable<boolean> = new Observable<boolean>((observer) => {
    observer.next(false);
    observer.complete();
  });

  get visibleColumns(): string[] {
    return this.allowedColumns.map((col) => col.valueOf());
  }

  trackByUserId(_index: number, item: UserListItem): string {
    return item.id;
  }

  onMembershipToggle(user: UserListItem): void {
    if (user.isMemberOfWorkspace !== undefined) {
      this.membershipToggle.emit(user);
    }
  }
}
